import { AlertTriangle, CheckCircle2, HelpCircle, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { translateImageLinkHeadline } from "@/lib/import/image-link-i18n";
import { ImageLinkReport, ImageLinkRules } from "@/api/image-link";

/**
 * Le compte rendu chiffre.
 *
 * La phrase d'en-tete est **le** livrable de l'exigence : « 300 de vos 400 images
 * correspondent clairement, voici les autres ». Elle est redigee cote serveur et
 * affichee telle quelle.
 *
 * Les regles sont des **interrupteurs vivants** : chaque bascule relance
 * l'analyse et les compteurs bougent. Le client voit « 300 → 362 » quand il
 * autorise l'equivalence des zeros de tete. Ce sont ces regles qui transforment
 * un taux de 75 % en 92 %, et le laisser piloter vaut mieux que n'importe quelle
 * heuristique cachee.
 */
export const ImageLinkMatchReport = ({
  report,
  rules,
  onRulesChange,
  analysing,
  onDownloadUnlinkable,
}: {
  report: ImageLinkReport;
  rules: ImageLinkRules;
  onRulesChange: (rules: ImageLinkRules) => void;
  analysing: boolean;
  onDownloadUnlinkable: () => void;
}) => {
  const vt = useVisibleT();
  const { t } = useTranslation();

  const toggles: Array<{ key: keyof ImageLinkRules; label: string }> = [
    {
      key: "ignoreLeadingZeros",
      label: vt("bo.imageLink.rule.leadingZeros", "Ignorer les zéros initiaux (0042 = 42)"),
    },
    {
      key: "ignoreSuffixes",
      label: vt("bo.imageLink.rule.suffixes", "Ignorer les suffixes (_recto, _1, _HD…)"),
    },
    {
      key: "searchInName",
      label: vt("bo.imageLink.rule.searchInName", "Chercher le numéro dans le nom"),
    },
  ];

  const counters = [
    {
      icon: <CheckCircle2 className="w-4 h-4 text-green-600" aria-hidden />,
      value: report.linkedAuto,
      label: vt("bo.imageLink.report.exact", "correspondance sûre"),
      hint: vt("bo.imageLink.report.exactHint", "prêtes à lier"),
    },
    {
      icon: <HelpCircle className="w-4 h-4 text-amber-600" aria-hidden />,
      value: report.needsDecision,
      label: vt("bo.imageLink.report.approx", "à valider"),
      hint: vt("bo.imageLink.report.approxHint", "correspondance approximative"),
    },
    {
      icon: <XCircle className="w-4 h-4 text-gray-400" aria-hidden />,
      value: report.unlinkable,
      label: vt("bo.imageLink.report.none", "non liables"),
      hint: vt("bo.imageLink.report.noneHint", "aucun numéro correspondant"),
    },
  ];

  const rate =
    report.filesSelected > 0
      ? Math.round((report.linkedAuto / report.filesSelected) * 100)
      : 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <p className="text-lg font-semibold">
          {translateImageLinkHeadline(t, report.headline)}
        </p>
        <Progress value={rate} className="h-2" />
        <span className="text-sm text-gray-500 tabular-nums">{rate} %</span>
      </div>

      <ul className="flex flex-col gap-1.5">
        {counters.map((counter) => (
          <li key={counter.label} className="flex items-center gap-2 text-sm">
            {counter.icon}
            <span className="font-semibold tabular-nums w-10 text-right">
              {counter.value}
            </span>
            <span>{counter.label}</span>
            <span className="text-gray-500">— {counter.hint}</span>
          </li>
        ))}
      </ul>

      <div className="flex flex-col gap-2 pt-1">
        <span className="text-sm font-medium">
          {vt("bo.imageLink.rules.title", "Règles de rapprochement")}
        </span>
        <div className="flex flex-wrap gap-2">
          {toggles.map((toggle) => {
            const active = rules[toggle.key];
            return (
              <Badge
                key={toggle.key}
                variant={active ? "default" : "secondary"}
                className={cn(
                  "cursor-pointer font-normal",
                  analysing && "opacity-50 pointer-events-none",
                )}
                onClick={() =>
                  onRulesChange({ ...rules, [toggle.key]: !active })
                }
              >
                {toggle.label}
              </Badge>
            );
          })}
        </div>
        <span className="text-xs text-gray-500">
          {vt(
            "bo.imageLink.rules.hint",
            "Activez ou désactivez une règle : les compteurs se recalculent.",
          )}
        </span>
      </div>

      {report.duplicateRegistrationNumbersInInstance > 0 && (
        <div className="flex gap-2 p-3 rounded-xl border border-amber-300 bg-amber-50 text-sm text-amber-900">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
          <span>
            {report.duplicateRegistrationNumbersInInstance}{" "}
            {vt(
              "bo.imageLink.report.duplicates",
              "numéros d'enregistrement sont en double dans votre base, ce qui empêche le rapprochement automatique des images concernées.",
            )}
          </span>
        </div>
      )}

      {report.unlinkable > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">
            {vt("bo.imageLink.report.unlinkableTitle", "Images non liables")}
          </span>
          <ul className="max-h-32 overflow-y-auto text-xs text-gray-600 flex flex-col gap-0.5">
            {report.unlinkableFiles.slice(0, 50).map((entry) => (
              <li key={entry.fileName} className="flex justify-between gap-2">
                <span className="font-mono truncate">{entry.fileName}</span>
                <span className="shrink-0">
                  {vt(
                    `bo.imageLink.reason.${entry.reason}`,
                    REASON_LABELS[entry.reason] ?? entry.reason,
                  )}
                </span>
              </li>
            ))}
          </ul>
          <Button
            variant="ghost"
            size="sm"
            className="self-start px-0 underline hover:bg-transparent"
            onClick={onDownloadUnlinkable}
          >
            {vt("bo.imageLink.report.downloadUnlinkable", "Télécharger la liste (.csv)")}
          </Button>
        </div>
      )}

      {report.filesRejected > 0 && (
        <p className="text-xs text-gray-500">
          {report.filesRejected}{" "}
          {vt(
            "bo.imageLink.report.rejected",
            "fichiers ont été écartés (format non accepté, vide, ou en double dans votre sélection).",
          )}
        </p>
      )}
    </div>
  );
};

/** Libelles de repli des raisons de non-rapprochement. */
const REASON_LABELS: Record<string, string> = {
  noMatch: "aucun numéro correspondant",
  unsupportedType: "format non accepté",
  emptyFile: "fichier vide",
  tooLarge: "fichier trop volumineux",
  duplicateInSelection: "doublon dans votre sélection",
};
